import { FeatureFlagTypes, NavigationStyles } from '@generated/graphql';
import { useFlags } from '@onevesthq/feature-flags/web';
import { UserContext } from '@providers/userContextProvider';
import { filterEnabledRoutes } from '@utils/filterEnabledRoutes';
import filterHouseholdsWithAccess from '@utils/filterHouseholdsWithAccess';
import { BootSpinner } from 'hephaestus/web';
import { useContext, useEffect, useMemo } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { useMenuMap } from './hooks/useMenuMap';
import { useOrganizationFeatureFlag } from './hooks/useOrganizationFeatureFlag';
import {
  accessiblePagesForRoutes,
  applyRuntimeFallback,
  ROUTE_FLAGS,
  routeMap,
} from './routeMap';

export const fallbackRedirectPath = (
  firstPath: string | undefined,
  hasHomePage: boolean,
  skipMenuRedirect = false,
) => {
  if (skipMenuRedirect && !hasHomePage) {
    return undefined;
  }
  if (hasHomePage) {
    return firstPath ?? '/';
  }
  if (!firstPath || firstPath === '/') {
    return undefined;
  }
  return firstPath;
};

export const Router = () => {
  const { userContext, activeEntity, accessiblePages } =
    useContext(UserContext);
  const featureFlags = useFlags(ROUTE_FLAGS);
  const { firstAccessibleMenuPath } = useMenuMap();
  const configurableHomePagesEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.ConfigurableHomePages,
  );
  const hasHomePage = !!(
    userContext.role?.homePageConfiguration && configurableHomePagesEnabled
  );

  const households = filterHouseholdsWithAccess(
    activeEntity?.households || [],
    activeEntity?.id,
    userContext,
  );
  const isHouseholdDefaultView =
    !!userContext.role?.makeHouseholdDefaultView && households.length > 0;

  // Simple + entity: entityMenu owns /clients/:id. A /clients hop flashes the table (OVT-5010).
  const skipMenuRedirect =
    userContext.role?.navigationStyle === NavigationStyles.Simple &&
    !!activeEntity?.id;
  const redirectTo = fallbackRedirectPath(
    firstAccessibleMenuPath,
    hasHomePage,
    skipMenuRedirect,
  );

  const router = useMemo(() => {
    const fallbackRoute = redirectTo ? (
      <Navigate to={redirectTo} replace />
    ) : null;
    const enabledRoutes = filterEnabledRoutes(routeMap, {
      featureFlags,
      accessiblePages: accessiblePagesForRoutes(
        accessiblePages,
        isHouseholdDefaultView,
      ),
    });
    // v7 dropped RouterProvider fallbackElement; lazy routes need a root hydrate fallback
    return createBrowserRouter(
      applyRuntimeFallback(enabledRoutes, hasHomePage, fallbackRoute).map(
        (route) => ({ ...route, hydrateFallbackElement: <BootSpinner /> }),
      ),
    );
  }, [
    accessiblePages,
    featureFlags,
    hasHomePage,
    isHouseholdDefaultView,
    redirectTo,
  ]);

  // Dispose the replaced/unmounted router so stale routers don't leak or navigate.
  useEffect(() => () => router.dispose(), [router]);

  return <RouterProvider router={router} />;
};
